import { FC, useContext, useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Box, Button, Grid, Typography } from '@mui/material'

import { getInitialExpandedContainers } from '@components/DisplayForm/helpers'
import {
  useCreateObjectDataRecordWithFixedKey,
  useDisplayFormState,
  useUpdateObjectDataRecordWithFixedKey,
} from '@components/DisplayForm/hooks'

import { setOpenedContainerForm } from '@redux/reducers/formStates.reducer'

import { useAppDispatch, useAppSelector, useKeyPress, useSubscribeEvent } from '@hooks'
import { buttonStylesByMode } from '@helpers'
import { COLLAPSED_CONTAINER_HEIGHT, FORM_TYPE, HOT_KEY, TAB_PANEL_HEIGHT } from '@constants'

import { EVENT_NAME, publish } from '../../../../events'
import { FormContext } from '../../DisplayForm'
import { Row } from '../../types'
import { ConfiguredFormElements } from '../ConfiguredFormElements'

type DisplayFormContentProps = {
  methods: UseFormReturn<Row, any>
  isDirty: boolean
  isValid?: boolean
  isSectionWindow?: boolean
}

export const DisplayFormContent: FC<DisplayFormContentProps> = ({
  methods,
  isDirty,
  isValid,
  isSectionWindow,
}) => {
  const {
    form,
    type,
    isViewEdit,
    handleEditOrSaveClick,
    handleCancel: onCancel,
    isDeleteRow,
    isFetchingFormError,
    globalId,
    row,
    skipHotKeyPress,
    isDropdownWindowOpenFormState,
    containers,
    onSetWindowTitle,
    dialogId,
    isSaveByEnterDisabled,
    isPendingCreateOrUpdateForm,
  } = useContext(FormContext)

  const { isFormDialogWindow } = useDisplayFormState()

  const [, { isLoading: isLoadingUpdateRow }] = useUpdateObjectDataRecordWithFixedKey(globalId)

  const [, { isLoading: isLoadingCreateRow }] = useCreateObjectDataRecordWithFixedKey(globalId)

  const formStates = useAppSelector(state => state.formStates)
  const dispatch = useAppDispatch()
  const [expandedContainers, setExpandedContainers] = useState(
    getInitialExpandedContainers(form?.code, formStates, containers)
  )

  const parentRef = useRef<HTMLDivElement>(null)

  const containersCount = containers.length
  const availableFormHeight = parentRef.current
    ? parentRef?.current?.getBoundingClientRect().height -
      COLLAPSED_CONTAINER_HEIGHT * containersCount -
      TAB_PANEL_HEIGHT
    : 0

  const { t } = useTranslation()

  const handleToggleExpandedContainers = (expanded: boolean) => {
    publish(EVENT_NAME.CONFIGURED_FORM_TOGGLE_OPENED_CONTAINERS, {
      expanded: !expanded,
      dialogId,
    })

    containers.forEach(container => {
      if (!container.expandable) return
      dispatch(
        setOpenedContainerForm({
          containerId: container.id,
          formCode: container.formCode,
          open: !expanded,
        })
      )
    })

    setExpandedContainers(!expanded)
  }

  const [activeElementBeforModeTrigger, setActiveElementBeforModeTrigger] =
    useState<Element | null>(null)

  const { keyPressed: changeModeFormTrigger } = useKeyPress(
    HOT_KEY.ENTER,
    undefined,
    skipHotKeyPress || isDropdownWindowOpenFormState || isSaveByEnterDisabled,
    'shiftKey'
  )
  const { keyPressed: exitEditModeFormTrigger, setKeyPressed: setExitEditModeKeyPressed } =
    useKeyPress(HOT_KEY.ESCAPE, undefined, skipHotKeyPress || isDropdownWindowOpenFormState)

  useEffect(() => {
    if (changeModeFormTrigger && (!isLoadingUpdateRow || !isLoadingCreateRow)) {
      setActiveElementBeforModeTrigger(document.activeElement)
      handleEditOrSaveClick()

      return
    }
    if (exitEditModeFormTrigger && !changeModeFormTrigger) {
      setTimeout(() => (activeElementBeforModeTrigger as HTMLElement)?.focus(), 0)

      handleCancel()
      setExitEditModeKeyPressed(false)

      return
    }
  }, [exitEditModeFormTrigger, changeModeFormTrigger])

  useEffect(() => {
    if (form && !isFetchingFormError && containers.length > 0) {
      containers.forEach((container, index) => {
        if (index === 0) {
          const hasUserParams = Boolean(formStates[container.formCode]?.containers[container.id])

          if (!hasUserParams || !container.expandable) {
            dispatch(
              setOpenedContainerForm({
                containerId: container.id,
                formCode: container.formCode,
                open: true,
              })
            )
            setExpandedContainers(true)
          }

          return
        }

        if (container.expandable) return

        dispatch(
          setOpenedContainerForm({
            containerId: container.id,
            formCode: container.formCode,
            open: true,
          })
        )
      })

      setResetFormKey(uuid())
    }
  }, [form, isFetchingFormError, containers.length])

  useSubscribeEvent(EVENT_NAME.CONFIGURED_FORM_HAS_OPENED_CONTAINER, data => {
    setExpandedContainers(data.detail)
  })

  const isCreateOrEdit: boolean = type === FORM_TYPE.EDIT || type === FORM_TYPE.CREATE || isViewEdit

  const [resetFormKey, setResetFormKey] = useState(uuid())

  const handleCancel = () => {
    onCancel()
  }

  const handleScroll = () => {
    // Нужно для скрытия дропдауна у autocomplete
    // Решение было получено в ходе решения этой проблемы:
    // https://github.com/mui/material-ui/issues/25564
    const documentActiveElement = document.activeElement as HTMLElement

    if (documentActiveElement.classList.contains('MuiAutocomplete-input')) {
      documentActiveElement.blur()
    }
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isDeleteRow ? (
        <Typography textAlign={'center'}>{t('form.deleteRow')}</Typography>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            height: '100%',
            mb: 0,
          }}
          onScroll={handleScroll}
        >
          {isFetchingFormError ? (
            <Typography align='center'>{t('form.noForm')}</Typography>
          ) : (
            <ConfiguredFormElements key={resetFormKey} availableFormHeight={availableFormHeight} />
          )}
        </Box>
      )}

      <Box
        sx={{
          ...(!isSectionWindow && {
            bottom: '2px',
            left: '2px',
            right: '2px',
            boxShadow: theme => theme.shadows[4],
            px: 1,
            position: 'fixed',
          }),
          ...(isSectionWindow && {
            position: 'relative',
            paddingLeft: '1px',
          }),
          bgcolor: theme => theme.palette.background.paper,
          pt: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Grid
          container
          display='flex'
          flexDirection='row'
          flexWrap={'nowrap'}
          justifyContent='space-between'
        >
          <Box mb='10px' mr='8px'>
            <Button
              sx={theme => ({
                ...buttonStylesByMode(theme, isFormDialogWindow),
                ml: '3px',
              })}
              onClick={() => handleToggleExpandedContainers(expandedContainers)}
            >
              {!expandedContainers ? t('form.show') : t('form.hide')}
            </Button>
          </Box>
          <Box mb='10px' ml='8px' mr='8px' whiteSpace='nowrap'>
            {isCreateOrEdit && (
              <Button
                disabled={isViewEdit ? false : !isDirty}
                sx={theme => ({
                  ...buttonStylesByMode(theme, isFormDialogWindow),
                  mr: isCreateOrEdit ? '15px' : '3px',
                })}
                onClick={handleCancel}
              >
                {t('form.cancel')}
              </Button>
            )}
            <Button
              disabled={(!isDirty || !isValid) && (type !== FORM_TYPE.VIEW || isViewEdit)}
              sx={theme => ({
                ...buttonStylesByMode(theme, isFormDialogWindow),
                mr: '3px',
              })}
              onClick={handleEditOrSaveClick}
            >
              {isCreateOrEdit ? t('form.save') : t('form.edit')}
            </Button>
          </Box>
        </Grid>
      </Box>
    </Box>
  )
}
